/**
 * L'écran d'accueil.
 *
 * Cadré avec l'utilisateur le 07/09/2026 une fois la bêta ouverte — jusque-là
 * `accueil` était le seul module marqué `ouvert: false`, et l'on arrivait sur
 * l'Annuaire, ce qui n'avait jamais été un choix.
 *
 * REFAIT LE 08/09/2026 d'après `docs/maquettes/accueil.html`, la seule maquette
 * qui fasse foi : le code écrit le 07/09 au soir a été perdu avec l'espace de
 * travail où il vivait, et n'avait jamais été transféré.
 *
 *
 * QUATRE BANDES, ET AUCUN TABLEAU
 * ---------------------------------------------------------------------------
 *   1. le résumé en une phrase, et les douze actions ;
 *   2. les quatre chiffres du mois ;
 *   3. huit vignettes de comptage, en trois groupes ;
 *   4. l'analyse, encore vide.
 *
 * Une première version (07/09) portait cinq tableaux de travail. Elle a été
 * refusée — « trop surchargé ». Ce qui reste des listes est donc un COMPTEUR
 * par vignette, et le clic emmène au module qui sait les traiter. Décision de
 * l'utilisateur du 08/09.
 *
 *
 * CE QUI N'AFFICHE RIEN RESTE AFFICHÉ — décision de l'utilisateur du 08/09
 * ---------------------------------------------------------------------------
 * Les trois vignettes grises de facturation et la bande « Analyse » ne portent
 * aucun chiffre. Elles restent : on voit où va le produit. C'est une
 * divergence assumée avec le cadrage §7, qui interdit les commentaires
 * explicatifs dans l'interface, et elle a été posée avant d'être appliquée.
 *
 *
 * LE CARROUSEL NE TOURNE PAS TOUT SEUL
 * ---------------------------------------------------------------------------
 * Les points changent de phrase au clic, et rien ne bouge sans qu'on le
 * demande. Un accueil se lit en trois secondes ; une phrase qui se dérobe
 * pendant qu'on la lit est un défaut, pas une animation. Aucune information
 * n'est perdue si personne ne clique : les mêmes chiffres sont dans les
 * vignettes, juste en dessous.
 *
 *
 * DÉCOUPE HABITUELLE : `VueAccueil` dessine, `EcranAccueil` décide. C'est ce
 * qui permet de rendre la page hors navigateur, avec n'importe quel état.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  dateDite,
  margeDouteuse,
  moisDit,
  partAffretee,
  phrasesResume,
  prenomDit,
  type Phrase,
} from './accueil.js';
import { lireAccueil, type Accueil, type EchecApi } from './api.js';
import { euros } from './finance.js';

type Etat =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'prete'; readonly accueil: Accueil }
  | { readonly nom: 'panne'; readonly cause: string };

const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Cette ligne n’existe plus. Rechargez l’écran.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Ce nom est déjà pris.',
  invalide: 'Demande refusée.',
  conflit: 'Deux périodes se recouvrent.',
  encore_actif: 'Désactivez d’abord.',
  encore_rattache: 'Des fiches s’y rattachent encore.',
  ordre_annule: 'Cet ordre est annulé.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape.',
  etape_validee:
    'Une étape déjà validée ne se déplace plus, et ne perd plus son camion.',
  jour_sans_etape: 'Aucune étape ce jour-là.',
  etape_en_tournee: 'Une étape de cet ordre appartient à une tournée.',
  // LES DEUX REFUS DU BOUCLAGE D'UNE JOURNÉE, ajoutés le 14/09/2026. Cette
  // carte doit rester EXHAUSTIVE : c'est elle qui garantit qu'aucun refus de
  // l'API ne s'affichera un jour sous forme de code brut.
  journee_cloturee:
    'Cette journée est clôturée. Rouvrez-la avant de la corriger.',
  journee_sans_chainage:
    'La journée précédente de ce camion ne dit pas où il s’est arrêté : renseignez son lieu de fin d’abord.',
  litige_deja_ouvert:
    'Cette étape a déjà un litige ouvert : résolvez-le avant d’en ouvrir un autre.',
  litige_deja_resolu:
    'Ce litige est déjà résolu. Ouvrez-en un nouveau sur l’étape si l’incident se répète.',
  // LES SIX REFUS DE LA FACTURATION, ajoutés le 09/09/2026. Ils n'arrivent pas
  // sur cet écran — la carte reste exhaustive, c'est ce qui garantit qu'aucun
  // refus de l'API ne s'affichera un jour sous forme de code brut.
  litige_ouvert: 'Cet ordre porte un litige ouvert : il ne se facture pas.',
  deja_facture: 'Cet ordre est déjà porté par une facture.',
  rien_a_facturer: 'Aucun de ces ordres n’attend de facture.',
  facture_emise: 'Cette pièce est émise : elle se corrige par un avoir.',
  facture_brouillon: 'Un brouillon ne s’envoie pas : validez-le d’abord.',
  facture_vide: 'Une facture sans ligne ne se valide pas.',
  // AJOUTÉ LE 09/09/2026. Un ordre parti en facturation ne se modifie plus :
  // il faut le retirer de la facturation d'abord.
  ordre_en_facturation:
    'Cet ordre est parti en facturation : retirez-le de la facturation avant de le modifier.',
  // LES CINQ REFUS DU RECOUVREMENT, ajoutés le 10/09/2026. Chacun dit la SUITE,
  // pas seulement le refus : c'est ce qui distingue un état d'une saisie fautive.
  affectation_excessive:
    'Vous affectez plus que ce qui a été reçu : corrigez la ventilation.',
  piece_non_emise:
    'Cette pièce est un brouillon : validez-la avant de la régler ou de la relancer.',
  piece_autre_client: 'Cette pièce appartient à un autre client.',
  gel_deja_actif:
    'Ce client a déjà un gel en cours : levez-le avant d’en poser un autre.',
  gel_absent: 'Ce client n’a aucun gel en cours.',
  // LES DEUX REFUS DE L'ANNULATION DE VALIDATION, ajoutés le 10/09/2026.
  numero_pas_le_dernier:
    'Cette pièce ne porte pas le dernier numéro de sa série : lui rendre son numéro laisserait un trou. Corrigez-la par un avoir.',
  facture_reglee:
    'Un règlement est affecté à cette pièce : détachez-le avant d’annuler la validation.',
  // AJOUTÉ LE 10/09/2026, sur un 500 constaté EN PRODUCTION : la base
  // refusait déjà (relance_pieces pointe sur factures en `on delete
  // restrict`), mais son refus sortait en erreur interne et l'écran
  // parlait d'une panne.
  piece_relancee:
    'Une relance a été envoyée sur cette pièce : elle ne peut plus être supprimée. Corrigez-la par un avoir.',
  facture_sans_echeance:
    'Cette pièce n’a pas de date d’échéance : sans elle, le recouvrement ne saura jamais qu’elle est en retard.',
  // AJOUTÉ LE 11/09/2026 avec le rattachement Pennylane. Distinct de
  // `code_pris` : le geste de réparation n'est pas le même. Le message NOMME
  // ce qu'il faut regarder — sans quoi il faudrait ouvrir les neuf autres
  // fiches pour trouver celle qui tient déjà ce client.
  pennylane_deja_rattache:
    'Ce client Pennylane est déjà rattaché à une autre fiche de l’annuaire. Le plus souvent, c’est que deux fiches Stafa désignent le même client : vérifiez vos doublons.',
  // AJOUTÉS LE 11/09/2026 avec l'envoi d'une facture chez Pennylane. Deux
  // messages et pas un : le premier mène à un AUTRE ÉCRAN, le second demande
  // de corriger la pièce. L'écran ne propose pas la même suite.
  pennylane_client_non_rattache:
    'Le client de cette facture n’est pas rattaché à un client Pennylane. Allez dans Paramètres → Pennylane pour le rattacher, puis réessayez.',
  pennylane_impossible:
    'Cette facture n’a pas pu être envoyée chez Pennylane. Le détail est affiché sous ce message : il vient de Pennylane et dit ce qui ne va pas.',
  facture_pas_deposee:
    'Cette facture n\u2019est pas encore d\u00e9pos\u00e9e chez Pennylane : envoyez-la d\u2019abord.',
  facture_modifiee_depuis_apercu:
    'Cette facture a changé depuis le dernier aperçu. Recliquez « Aperçu Pennylane », vérifiez le document, puis validez.',
  // LES QUATRE REFUS DE L'AVOIR, ajoutés le 11/09/2026. Quatre et pas un :
  // chacun porte une SUITE différente — ouvrir l'avoir existant, corriger un
  // montant, ou rien du tout quand le geste n'a pas de sens.
  avoir_sur_avoir:
    'On n’établit pas un avoir sur un avoir. Si cet avoir est faux, c’est une facture qu’il faut établir.',
  facture_deja_avoiree:
    'Cette facture est déjà intégralement avoirée : un second avoir rendrait au client plus qu’il n’a payé. Ouvrez l’avoir existant.',
  avoir_trop_grand:
    'Cet avoir dépasse ce qui reste à avoirer sur cette facture. Le détail affiché sous ce message donne le montant maximal.',
  avoir_impossible:
    'Cet avoir ne peut pas être établi. Le détail affiché sous ce message dit ce qui s’y oppose.',
  // AJOUTÉ LE 10/09/2026. Distinct de `injoignable`, qui parlait de connexion
  // alors que le service avait bel et bien répondu.
  service:
    'Le service a répondu une erreur inattendue. Ce n’est pas votre connexion. Ouvrez la console (F12) : le code y est écrit.',
};

/** Où mène une action ou un lien : un module, et l'entrée de sous-menu voulue. */
export interface Cible {
  readonly module: string;
  readonly sousMenu?: string;
}

/**
 * LES DOUZE ACTIONS, dans l'ordre de la maquette validée.
 *
 * Trois colonnes de quatre. La première porte ce qui CRÉE (« + »), les deux
 * autres ce qui EMMÈNE (« › ») — la distinction est dans le signe, pas dans un
 * titre de plus.
 *
 * La grille CSS remplit LIGNE PAR LIGNE : la liste est donc écrite en
 * alternant les trois colonnes, et non colonne après colonne. Écrite dans
 * l'autre ordre, la page afficherait les quatre créations sur la première
 * ligne.
 *
 * « nouvel-ordre » n'est PAS une entrée de sous-menu : c'est le geste
 * « créer », qui vit dans l'écran des ordres. `Application` le sait et ouvre
 * cet écran sur sa liste, où le bouton de création se trouve.
 */
const ACTIONS: readonly { readonly nom: string; readonly cible: Cible }[] = [
  {
    nom: '+ Ordre de transport',
    cible: { module: 'exploitation', sousMenu: 'nouvel-ordre' },
  },
  { nom: '› Planning', cible: { module: 'exploitation', sousMenu: 'planning' } },
  { nom: '› Litiges', cible: { module: 'exploitation', sousMenu: 'litiges' } },

  {
    nom: '› Ordres de transport',
    cible: { module: 'exploitation', sousMenu: 'ordres' },
  },
  {
    nom: '› Parc et chauffeurs',
    cible: { module: 'exploitation', sousMenu: 'affectation' },
  },

  // L'annuaire et les ressources sont dans Exploitation depuis le lot 95.
  { nom: '+ Client', cible: { module: 'exploitation', sousMenu: 'clients' } },
  { nom: '› Affrètement', cible: { module: 'exploitation', sousMenu: 'affretement' } },
  { nom: '› Finance', cible: { module: 'finance' } },

  { nom: '+ Import de données', cible: { module: 'parametres', sousMenu: 'import' } },
  { nom: '› Emballage', cible: { module: 'exploitation', sousMenu: 'emballage' } },
  { nom: '› Paramètres', cible: { module: 'parametres' } },
];

/** Un chiffre du mois. `note` et `alerte` sont facultatifs. */
function Chiffre({
  libelle,
  valeur,
  note,
  alerte = false,
  lien,
  quandLien,
}: {
  readonly libelle: string;
  readonly valeur: string;
  readonly note?: string | null;
  readonly alerte?: boolean;
  readonly lien: string;
  readonly quandLien: () => void;
}) {
  return (
    <div className="chiffre-accueil">
      <p className="libelle-chiffre-accueil">{libelle}</p>
      <p className="valeur-chiffre-accueil">{valeur}</p>
      {/* LE TRAIT EST SOUS LES QUATRE MONTANTS, jamais sous un seul : une
          rangée où un seul chiffre est souligné se lit de travers. Ce qui
          distingue la marge douteuse est la COULEUR du trait, pas sa
          présence. */}
      <div
        className={
          alerte ? 'souligne-accueil souligne-accueil-alerte' : 'souligne-accueil'
        }
      />
      {note !== null && note !== undefined && (
        <p className="note-chiffre-accueil">{note}</p>
      )}
      <button className="lien-accueil" type="button" onClick={quandLien}>
        {`› ${lien}`}
      </button>
    </div>
  );
}

/** Une vignette de comptage. Sans `quandClic`, elle est grise et inerte. */
function Vignette({
  titre,
  valeur,
  quandClic,
}: {
  readonly titre: string;
  readonly valeur: string;
  readonly quandClic?: () => void;
}) {
  if (quandClic === undefined) {
    return (
      <div className="vignette-accueil vignette-accueil-a-venir">
        <p className="titre-vignette-accueil">{titre}</p>
        <p className="valeur-vignette-accueil">—</p>
        <p className="pied-vignette-accueil">bientôt</p>
      </div>
    );
  }
  return (
    <button className="vignette-accueil" type="button" onClick={quandClic}>
      <p className="titre-vignette-accueil">{titre}</p>
      <p className="valeur-vignette-accueil">{valeur}</p>
      <p className="pied-vignette-accueil">›</p>
    </button>
  );
}

export function VueAccueil({
  etat,
  aujourdhui,
  nomUtilisateur,
  phrase,
  phrases,
  quandPhrase,
  quandAller,
  quandReessayer,
}: {
  readonly etat: Etat;
  /** Passé en paramètre : une vue qui lit l'heure ne se dessine pas deux fois pareil. */
  readonly aujourdhui: Date;
  readonly nomUtilisateur: string;
  /** L'indice de la phrase montrée. Tenu par l'écran, pas par la vue. */
  readonly phrase: number;
  readonly phrases: readonly Phrase[];
  readonly quandPhrase: (indice: number) => void;
  readonly quandAller: (cible: Cible) => void;
  readonly quandReessayer: () => void;
}) {
  if (etat.nom === 'chargement') {
    return <main className="ecran-accueil">Chargement…</main>;
  }
  if (etat.nom === 'panne') {
    return (
      <main className="ecran-accueil">
        <p className="bandeau-refus">{etat.cause}</p>
        <button className="bouton" type="button" onClick={quandReessayer}>
          Réessayer
        </button>
      </main>
    );
  }

  const a = etat.accueil;
  const part = partAffretee(a.mois.affretes, a.mois.ordres);
  const doute = margeDouteuse(a.mois.ordres_sans_cout, a.mois.ordres);
  const prenom = prenomDit(nomUtilisateur);
  const restants = Math.max(a.journee.total - a.journee.faites, 0);
  // L'indice peut sortir de la liste quand un chiffre tombe à zéro entre deux
  // chargements : la phrase disparaît, l'indice ne bouge pas. On retombe sur
  // la première plutôt que de ne rien afficher.
  const montree = phrases[phrase] ?? phrases[0];

  return (
    <main className="ecran-accueil">
      {/* ---------------------------------------------------------------- */}
      {/* BANDE 1 — le résumé et les actions                                */}
      {/* ---------------------------------------------------------------- */}
      <section className="bande-accueil bande-haute-accueil">
        <div>
          <p className="etiquette-accueil">Résumé</p>
          <p className="phrase-accueil">
            {montree?.avant}
            {montree !== undefined && montree.fort !== '' && (
              <strong>{montree.fort}</strong>
            )}
            {montree?.apres}
          </p>
          {/* UN SEUL POINT NE SE MONTRE PAS : il n'indiquerait rien à
              choisir. */}
          {phrases.length > 1 && (
            <div className="points-accueil">
              {phrases.map((p, i) => (
                <button
                  key={`${p.avant}${p.fort}`}
                  type="button"
                  className={
                    i === phrase ? 'point-accueil point-accueil-actif' : 'point-accueil'
                  }
                  aria-label={`${p.avant}${p.fort}${p.apres}`}
                  onClick={() => quandPhrase(i)}
                />
              ))}
            </div>
          )}
          <p className="salutation-accueil">
            {prenom === ''
              ? dateDite(aujourdhui)
              : `Bonjour ${prenom} · ${dateDite(aujourdhui)}`}
          </p>
        </div>

        <div>
          <p className="etiquette-accueil">Actions</p>
          <nav className="actions-accueil">
            {ACTIONS.map((action) => (
              <button
                key={action.nom}
                type="button"
                className="action-accueil"
                onClick={() => quandAller(action.cible)}
              >
                {action.nom}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* BANDE 2 — les quatre chiffres du mois                             */}
      {/* ---------------------------------------------------------------- */}
      <section className="bande-accueil">
        <h2 className="titre-bande-accueil">{moisDit(aujourdhui)}</h2>
        <div className="chiffres-accueil">
          <Chiffre
            libelle="Chiffre d’affaires"
            valeur={euros(Number(a.mois.ca)) ?? '—'}
            note={`${String(a.mois.ordres)} transport${a.mois.ordres > 1 ? 's' : ''}`}
            lien="La finance"
            quandLien={() => quandAller({ module: 'finance' })}
          />
          <Chiffre
            libelle="Marge"
            valeur={euros(Number(a.mois.marge)) ?? '—'}
            note={doute}
            alerte={doute !== null}
            lien="La finance"
            quandLien={() => quandAller({ module: 'finance' })}
          />
          <Chiffre
            libelle="Kilomètres"
            valeur={
              a.mois.kilometres === 0
                ? '—'
                : new Intl.NumberFormat('fr-FR').format(a.mois.kilometres)
            }
            lien="Les ordres"
            quandLien={() => quandAller({ module: 'exploitation', sousMenu: 'ordres' })}
          />
          <Chiffre
            libelle="Part affrétée"
            valeur={part === null ? '—' : `${String(part)} %`}
            note={
              part === null
                ? null
                : `${String(a.mois.affretes)} ordre${a.mois.affretes > 1 ? 's' : ''} sous-traité${a.mois.affretes > 1 ? 's' : ''}`
            }
            lien="L’affrètement"
            quandLien={() =>
              quandAller({ module: 'exploitation', sousMenu: 'affretement' })
            }
          />
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* BANDE 3 — les huit vignettes                                      */}
      {/* ---------------------------------------------------------------- */}
      <section className="bande-accueil">
        <div className="groupes-accueil">
          <div>
            <p className="etiquette-accueil etiquette-groupe-accueil">Exploitation</p>
            <div className="vignettes-accueil vignettes-accueil-3">
              <Vignette
                titre="Arrêts à faire aujourd’hui"
                valeur={String(restants)}
                quandClic={() =>
                  quandAller({ module: 'exploitation', sousMenu: 'planning' })
                }
              />
              <Vignette
                titre="Ordres sans camion"
                valeur={String(a.a_affecter.combien)}
                quandClic={() =>
                  quandAller({ module: 'exploitation', sousMenu: 'planning' })
                }
              />
              <Vignette
                titre="Ordres sans activité"
                valeur={String(a.non_classes)}
                quandClic={() =>
                  quandAller({ module: 'exploitation', sousMenu: 'ordres' })
                }
              />
            </div>
          </div>

          <div>
            <p className="etiquette-accueil etiquette-groupe-accueil">À traiter</p>
            <div className="vignettes-accueil vignettes-accueil-2">
              <Vignette
                titre="Litiges ouverts"
                valeur={String(a.litiges.combien)}
                quandClic={() =>
                  quandAller({ module: 'exploitation', sousMenu: 'litiges' })
                }
              />
              <Vignette
                titre={
                  a.bons.combien === 0
                    ? 'Bons expirés'
                    : `Bons expirés · ${String(a.bons.palettes)} palette${a.bons.palettes > 1 ? 's' : ''}`
                }
                valeur={String(a.bons.combien)}
                quandClic={() =>
                  quandAller({ module: 'exploitation', sousMenu: 'emballage' })
                }
              />
            </div>
          </div>

          {/* SANS `quandClic`, la vignette est grise et inerte : elle tient la
              place de la facturation, qui est l'étape 10 du cadrage. */}
          <div>
            <p className="etiquette-accueil etiquette-groupe-accueil">Facturation</p>
            <div className="vignettes-accueil vignettes-accueil-3">
              <Vignette titre="À facturer" valeur="—" />
              <Vignette titre="Factures impayées" valeur="—" />
              <Vignette titre="Relances à envoyer" valeur="—" />
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* BANDE 4 — l'analyse                                               */}
      {/* ---------------------------------------------------------------- */}
      <section className="bande-accueil">
        <h2 className="titre-bande-accueil">Analyse</h2>
        <div className="analyses-accueil">
          <div>
            <h3 className="titre-analyse-accueil">Vos cinq premiers clients</h3>
            <p className="attente-analyse-accueil">
              Le classement par chiffre d’affaires viendra avec le module Finance.
            </p>
          </div>
          <div>
            <h3 className="titre-analyse-accueil">L’année en cours</h3>
            <p className="attente-analyse-accueil">
              La courbe du chiffre d’affaires et de la marge, mois par mois, viendra
              avec le lot 3 du coût de revient.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}

export function EcranAccueil({
  jeton,
  nomUtilisateur,
  quandAccesPerdu,
  quandAller,
}: {
  /**
   * LE JETON SE DEMANDE, IL NE SE GARDE PAS : c'est une fonction, comme dans
   * tous les autres écrans. Un jeton passé en chaîne serait celui du premier
   * rendu, et il expire au bout d'une heure.
   */
  readonly jeton: () => Promise<string | null>;
  readonly nomUtilisateur: string;
  readonly quandAccesPerdu: () => void;
  readonly quandAller: (cible: Cible) => void;
}) {
  const [etat, setEtat] = useState<Etat>({ nom: 'chargement' });
  const [phrase, setPhrase] = useState(0);

  const charger = useCallback(() => {
    setEtat({ nom: 'chargement' });
    void (async () => {
      const j = await jeton();
      if (j === null) return;
      const resultat = await lireAccueil(j);
      if (resultat.etat === 'ok') {
        setEtat({ nom: 'prete', accueil: resultat.valeur });
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setEtat({ nom: 'panne', cause: REFUS[resultat.cause] });
    })();
  }, [jeton, quandAccesPerdu]);

  useEffect(charger, [charger]);

  /**
   * L'ÂGE DU PLUS ANCIEN LITIGE se lit sur la PREMIÈRE ligne rendue.
   *
   * Ce n'est pas une supposition : la requête de `apps/api/src/accueil.ts`
   * ordonne `order by l.ouvert_le`, du plus ancien au plus récent. Si cet
   * ordre changeait un jour, la phrase deviendrait fausse en silence — d'où ce
   * commentaire, à côté de la seule ligne qui en dépend.
   */
  const phrases =
    etat.nom === 'prete'
      ? phrasesResume({
          journee: {
            faites: etat.accueil.journee.faites,
            total: etat.accueil.journee.total,
          },
          aAffecter: etat.accueil.a_affecter.combien,
          litiges: {
            combien: etat.accueil.litiges.combien,
            plusAncienJours: etat.accueil.litiges.lignes[0]?.jours ?? null,
          },
          bons: {
            combien: etat.accueil.bons.combien,
            palettes: etat.accueil.bons.palettes,
          },
        })
      : [];

  return (
    <VueAccueil
      etat={etat}
      aujourdhui={new Date()}
      nomUtilisateur={nomUtilisateur}
      phrase={phrase}
      phrases={phrases}
      quandPhrase={setPhrase}
      quandAller={quandAller}
      quandReessayer={charger}
    />
  );
}
